import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from '../../../components/ui/notify';
import { Icon } from '@iconify/react';
import NoData from '../../../components/common/NoData';
import { getPointsCheckoutPreview, processPayUPayment } from '../../../services/user/course';

const REDIRECT_DELAY_MS = 2000;

const ensureMinLoading = async (startTime, minMs = REDIRECT_DELAY_MS) => {
  const remaining = minMs - (Date.now() - startTime);
  if (remaining > 0) {
    await new Promise((resolve) => setTimeout(resolve, remaining));
  }
};

// Helper function to get currency symbol
const getCurrencySymbol = (currency) => {
  switch (currency?.toUpperCase()) {
    case 'INR':
      return '₹';
    case 'USD':
      return '$';
    case 'SGD':
      return 'S$';
    case 'EUR':
      return '€';
    case 'GBP':
      return '£';
    default:
      return '$'; // Default to USD symbol
  }
};

function OrderDetailsWithPayu() {
  const location = useLocation();
  const navigate = useNavigate();
  const courseData = location.state;

  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [pointsApplied, setPointsApplied] = useState(false);

  useEffect(() => {
    if (!location.state || !location.state.course_id) {
      // No course data – component will show "No order data available"
    }
  }, [location.state, courseData]);

  useEffect(() => {
    if (!courseData?.course_id) return;
    getPointsCheckoutPreview(courseData.course_id)
      .then((response) => {
        setWallet(response?.data || null);
        setPointsApplied(false);
      })
      .catch(() => setWallet(null));
  }, [courseData?.course_id]);



  const subtotal = Number(courseData?.course_price) || 0;
  const maxPoints = Math.min(Number(wallet?.availablePoints || 0), Number(wallet?.maxPointsApplicable || 0));
  const canUsePoints = maxPoints > 0;
  const rate = Number(wallet?.pointsPerCurrencyUnit || 50);
  const appliedPoints = pointsApplied && canUsePoints ? maxPoints : 0;
  const pointsDiscount = appliedPoints / rate;
  const maxSavings = maxPoints / rate;
  const total = Math.max(0, subtotal - pointsDiscount);

  useEffect(() => {
    if (!canUsePoints) setPointsApplied(false);
  }, [canUsePoints]);

  useEffect(() => {
    const resetCheckoutLoading = () => setIsCheckoutLoading(false);
    window.addEventListener('pageshow', resetCheckoutLoading);
    return () => window.removeEventListener('pageshow', resetCheckoutLoading);
  }, []);

  const handlePayUCheckout = async () => {
    setIsCheckoutLoading(true);
    const startTime = Date.now();

    try {
      const response = await processPayUPayment({
        origin: window.location.origin,
        course_id: courseData.course_id,
        selected_points: appliedPoints,
      });

      const payload = response?.data ?? response;

      if (response?.success && payload?.enrolled) {
        await ensureMinLoading(startTime);
        toast.success('Course purchased with points. You are now enrolled.');
        navigate('/user/courses');
        return;
      }

      if (response?.success && payload?.paymentData && payload?.payuUrl) {
        await ensureMinLoading(startTime);

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = payload.payuUrl;
        form.style.display = 'none';

        Object.keys(payload.paymentData).forEach((key) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = payload.paymentData[key];
          form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
        return;
      }

      await ensureMinLoading(startTime);
      toast.error(response?.message || response?.error_msg || 'Failed to process payment. Please try again.');
      setIsCheckoutLoading(false);
    } catch (error) {
      await ensureMinLoading(startTime);
      toast.error('Failed to process payment.');
      setIsCheckoutLoading(false);
    }
  };

  if (!courseData || !courseData.course_id) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center" style={{ minHeight: '60vh' }}>
        <div>
          <NoData
            message="No order data available"
            description="Please select a course to purchase"
            icon="fluent:shopping-cart-24-regular"
          />
          <div className="tw-text-center tw-pb-6">
            <button
              className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary tw-px-6 tw-py-2 tw-rounded-full"}
              onClick={() => navigate(-1)}
            >
              <Icon icon="fluent:arrow-left-24-regular" className="tw-me-2" />
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-ml-auto tw-mr-auto tw-w-full tw-max-w-[80rem] tw-pl-4 tw-pr-4 ui-container tw-py-2">
      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-justify-center"}>
        <div className="tw-min-w-[0px] ui-column lg:tw-w-2/3">
          {/* Header */}
          <div className="tw-text-left tw-mb-4">
            <h1 className="display-6 tw-font-bold tw-text-slate-900 tw-mb-2">Order Details</h1>
            <div className="tw-flex tw-justify-start tw-items-center">
              <div className="!tw-bg-[var(--primary-color)] tw-bg-brand-600 tw-rounded-full tw-px-4 tw-py-1">
                <small className="![font-weight:var(--font-weight-medium)] tw-text-white tw-font-medium">
                  <Icon icon="fluent:lock-24-regular" className="tw-me-1" />
                  Secure Payment Gateway
                </small>
              </div>
            </div>
          </div>

          {/* Single Card Design */}
          <div className={"[&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card"} style={{ borderRadius: '20px' }}>
            <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-p-5 tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body">
              {/* Course Information Section */}
              <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-mb-12"}>
                <div className="tw-min-w-[0px] ui-column md:tw-w-1/3 tw-text-center">
                  <div className="tw-relative tw-mb-4">
                    <img
                      src={courseData.banner_image}
                      alt={courseData.course_name}
                      className="tw-max-w-full tw-rounded-xl"
                      style={{
                        height: '200px',
                        width: '100%',
                        objectFit: 'cover',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/placeholder-course-image.jpg';
                      }}
                    />
                    <div className="tw-absolute tw-top-0 tw-end-0 tw-m-2">
                      <span className={"!tw-bg-[var(--primary-color)] [&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-inline-flex tw-items-center tw-gap-1 tw-text-[0.75rem] tw-font-medium tw-leading-[1rem] ui-badge tw-bg-brand-600 tw-px-2 tw-py-1 tw-rounded-full"}>
                        <Icon icon="fluent:graduation-cap-24-regular" className="tw-me-1" />
                        {courseData.course_type}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="tw-min-w-[0px] ui-column md:tw-w-2/3">
                  <h3 className="tw-font-bold tw-text-slate-900 tw-mb-4">{courseData.course_name}</h3>
                  <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-6" style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>{courseData.course_desc}</p>
                </div>
              </div>

              <hr className="tw-my-6" />

              {/* Payment Summary Section */}
              <div className="tw-mb-6">
                <div className="tw-flex tw-items-center tw-mb-6">
                <div className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] tw-bg-opacity-10 tw-rounded-full tw-p-1 tw-me-4 tw-border tw-border-brand-600"}>
                    <Icon icon="mdi:credit-card" className="!tw-text-[var(--primary-color)] tw-text-xl tw-text-brand-600" />
                  </div>
                  <div>
                    <h4 className="tw-font-bold tw-text-slate-900 tw-mb-1">Payment Summary</h4>
                    <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-0">Complete your purchase securely</p>
                  </div>
                </div>

                <div className="payment-breakdown tw-mb-6">
                  <div className="tw-flex tw-justify-between tw-items-center tw-py-4 tw-border-b">
                    <div className="tw-flex tw-items-center">
                      <Icon icon="fluent:book-24-regular" className="!tw-text-[var(--primary-color)] tw-text-brand-600 tw-me-2" />
                      <span className="![font-weight:var(--font-weight-medium)] tw-font-medium">Course Price</span>
                    </div>
                    <span className="tw-font-bold">{getCurrencySymbol(courseData?.currency)}{subtotal.toFixed(2)}</span>
                  </div>

                  {wallet && (
                    <div className="tw-flex tw-justify-between tw-items-center tw-py-4 tw-border-b">
                      <div className="tw-flex tw-items-center min-w-0">
                        <Icon
                          icon={
                            pointsApplied && canUsePoints
                              ? 'fluent:checkmark-circle-24-filled'
                              : 'fluent:coins-24-filled'
                          }
                          className={`tw-me-2 tw-shrink-0   ${
                            !canUsePoints
                              ? "!tw-text-[var(--secondary-color)] tw-text-slate-500"
                              : pointsApplied
                                ? "!tw-text-[var(--success-color)] tw-text-emerald-600"
                                : "!tw-text-[var(--primary-color)] tw-text-brand-600"
                          }`}
                        />
                        <div className="min-w-0">
                          <span className={`![font-weight:var(--font-weight-medium)] tw-font-medium   ${!canUsePoints ? "!tw-text-[var(--secondary-color)] tw-text-slate-500" : ""}`}>
                            {pointsApplied && canUsePoints ? 'Points applied' : 'Reward points'}
                          </span>
                          <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-sm tw-text-slate-500">
                            {!canUsePoints ? (
                              'No points available'
                            ) : pointsApplied ? (
                              <>
                                {appliedPoints.toLocaleString()} pts
                                {pointsDiscount > 0 && (
                                  <span className="!tw-text-[var(--success-color)] tw-text-emerald-600">
                                    {' '}
                                    · save {getCurrencySymbol(courseData?.currency)}
                                    {pointsDiscount.toFixed(2)}
                                  </span>
                                )}
                              </>
                            ) : (
                              <>
                                {wallet.availablePoints.toLocaleString()} available · up to{' '}
                                {maxPoints.toLocaleString()} pts
                                {maxSavings > 0 && (
                                  <span className="!tw-text-[var(--success-color)] tw-text-emerald-600">
                                    {' '}
                                    (save {getCurrencySymbol(courseData?.currency)}
                                    {maxSavings.toFixed(2)})
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      {canUsePoints && (
                        <button
                          type="button"
                          className={`[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-font-medium [transition:all_0.2s_ease] tw-justify-center tw-gap-2 tw-border-[1px] tw-border-[transparent] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] tw-pt-1.5 tw-pb-1.5 tw-text-[0.75rem] tw-leading-[1rem] ui-button ui-button-sm tw-rounded-full tw-px-4 tw-shrink-0   ${
                            pointsApplied ? "[--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-button-outline-secondary" : "[&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button-primary"
                          }`}
                          onClick={() => setPointsApplied((value) => !value)}
                        >
                          {pointsApplied ? 'Remove' : 'Apply'}
                        </button>
                      )}
                    </div>
                  )}

                  <div className="tw-flex tw-justify-between tw-items-center tw-py-4 tw-border-b">
                    <span className="![font-weight:var(--font-weight-semibold)] tw-font-semibold">Remaining payment</span>
                    <span className="tw-font-bold tw-text-lg">{getCurrencySymbol(courseData?.currency)}{total.toFixed(2)}</span>
                  </div>

                </div>

                <div className="payment-actions">
                  <button
                    className={`[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] tw-pl-6 tw-pr-6 tw-text-[1rem] tw-leading-[1.5rem] ui-button ui-button-primary ui-button-lg tw-w-full tw-py-4 tw-rounded-full tw-shadow-sm tw-text-white${
                      isCheckoutLoading ? "   loading" : ""
                    }`}
                    onClick={handlePayUCheckout}
                    disabled={isCheckoutLoading}
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      background: isCheckoutLoading
                        ? undefined
                        : 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                      border: 'none',
                    }}
                  >
                    {isCheckoutLoading ? (
                      <>
                        <span
                          className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-me-2"
                          role="status"
                          aria-hidden="true"
                          style={{ borderColor: 'rgba(255,255,255,0.35)', borderRightColor: '#fff' }}
                        />
                        {total === 0 ? 'Purchasing with Points...' : 'Processing Payment...'}
                      </>
                    ) : (
                      <>
                        <Icon icon="fluent:lock-24-regular" className="tw-me-2" />
                        {total === 0 ? 'Purchase with Points' : `Pay ${getCurrencySymbol(courseData?.currency)}${total.toFixed(2)} Securely`}
                      </>
                    )}
                  </button>

                  <div className="tw-text-center tw-mt-4">
                    <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500">
                      <Icon icon="fluent:shield-24-regular" className="tw-me-1" />
                      Your payment information is secure and encrypted
                    </small>
                  </div>
                </div>


              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderDetailsWithPayu;
